import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { action, makeObservable, observable, runInAction } from "mobx";
import { Answer } from "models/Answer";
import { AnswerPersistenceMode } from "models/AnswerPersistenceMode";
import { AnswerTypeNames } from "models/AnswerType";
import LicensedModule from "models/LicensedModule";
import Question from "models/Question";
import { Tag } from "models/Tag";
import {
    ConversationService,
    IConversationModuleInfo,
} from "services/ConversationService";
import { ModuleService } from "services/ModuleService";
import { AuthStore } from "stores/AuthStore";
import { BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { AcxStore } from "stores/RootStore";
import { v4 as uuidv4 } from "uuid";
import { ConvoModuleUIModel } from "../questionComponents/ConvoModuleUIModel";
import { ModulePreview } from "components/Admin/Organizations/types/Module.type";
import { ServiceError } from "services/BaseService";

export const loadConvoModules = "Load Convo Module Info";
export const loadLicensedModule = "Load Selected Licensed Modules";
export const loadDataLabelModules = "Load Data Labeling Modules";
const createOrUpdateConvoModule = "Create Or Update Conversation Module";

@AcxStore
export default class ConversationLabelingStore extends BaseStore {
    readonly authStore: AuthStore;
    readonly moduleService: ModuleService = new ModuleService();
    readonly conversationService: ConversationService =
        new ConversationService();
    messageStore: MessageStore;

    @observable
    answers: Answer[] = [];

    @observable
    autofilledAnswerSnapshots: Map<string, string> = new Map();

    @observable
    audioMetadataId?: string;

    @observable
    uiModel: ConvoModuleUIModel;

    @observable
    selectedLicensedModuleId?: string;

    @observable
    selectedConversationModuleId?: string;

    @observable
    selectedLicensedModule?: LicensedModule;

    @observable
    conversationLicensedModules: ModulePreview[] = [];

    @observable
    conversationSafetyEventLicensedModules: ModulePreview[] = [];

    @observable
    convoModuleInfo: IConversationModuleInfo[] = [];

    @observable
    dataLabelingLockError: boolean = false;

    @observable
    hasDataLabelingLock: boolean = false;

    @observable
    lastLockAudioMetadataId?: string;

    @observable
    lastLockLicensedModuleId?: string;

    constructor(public rootStore: IRootStore) {
        super("ConversationLabelingStore");
        makeObservable(this);
        this.authStore = rootStore.getStore(AuthStore);
        this.messageStore = rootStore.getStore(MessageStore);
    }

    @action
    setAnswerForQuestion = (
        question: Question,
        tags: Tag[],
        answer: Answer | undefined,
        conversationModuleId?: string,
    ) => {
        if (answer === undefined) {
            const newAnswer = new Answer(
                undefined,
                question.id,
                question,
                undefined,
                this.authStore._user.profile.email,
                this.authStore._user.profile.email,
            );
            this.updateAnswer(
                newAnswer,
                question.id,
                newAnswer.conversationModuleId ?? conversationModuleId,
            );
            answer = newAnswer;
        } else {
            this.updateAnswer(
                answer,
                question.id,
                answer.conversationModuleId ?? conversationModuleId,
            );
        }
        answer.setAnswerTags(tags);

        return answer;
    };

    @action
    updateAnswer(
        answer: Answer,
        questionId: string | undefined,
        conversationModuleId: string | undefined,
    ) {
        if (questionId === undefined) {
            return;
        }

        answer.conversationModuleId = conversationModuleId;

        let curAns = this.answers.find(
            (a) => a.questionId === answer.questionId,
        );

        if (curAns) {
            Object.assign(curAns, answer);
        } else {
            this.answers = [...this.answers, answer];
        }
    }

    getAnswerForQuestion = (
        question: Question,
        conversationModuleId: string | undefined,
    ): Answer | undefined => {
        let ans: Answer | undefined = undefined;
        const answers = this.answers.filter(
            (value) =>
                value.questionId === question.id &&
                (!value.conversationModuleId ||
                    value.conversationModuleId === conversationModuleId),
        );

        if (answers.length > 0) {
            ans = answers[0];
        }

        ans?.setQuestion(question);
        return ans;
    };

    @action
    setFillInAnswerForQuestion = (
        question: Question,
        fillInAnswer: string,
        answer: Answer | undefined,
        conversationModuleId?: string,
    ) => {
        if (answer === undefined) {
            const newAnswer = new Answer(
                undefined,
                question.id,
                question,
                undefined,
                this.authStore._user.profile.email,
                this.authStore._user.profile.email,
                fillInAnswer,
            );

            this.updateAnswer(
                newAnswer,
                question.id,
                newAnswer.conversationModuleId ?? conversationModuleId,
            );
            answer = newAnswer;
        } else {
            answer.fillInAnswerValue = fillInAnswer;
            this.updateAnswer(
                answer,
                question.id,
                answer.conversationModuleId ?? conversationModuleId,
            );
        }

        return answer;
    };

    @action
    openLabelingDialog = () => {
        this.answers = [];
        this.autofilledAnswerSnapshots = new Map();
        this.dataLabelingLockError = false;
        this.selectedLicensedModuleId = undefined;
    };

    public async getConversationModuleInfo() {
        return await this.setupAsyncTask(loadConvoModules, async () => {
            // used for persisted answers after user selected a module
            const convoRes =
                await this.conversationService.getConversationModuleInfo(
                    this.audioMetadataId!,
                );

            runInAction(() => {
                this.convoModuleInfo = convoRes;
            });
        });
    }

    public async TryGetDataLabelingLock() {
        const result = await this.moduleService.TryGetDataLabelingLock(
            this.audioMetadataId!,
            this.selectedLicensedModuleId!,
        );
        runInAction(() => {
            this.hasDataLabelingLock = result;
            if (result) {
                this.lastLockAudioMetadataId = this.audioMetadataId;
                this.lastLockLicensedModuleId = this.selectedLicensedModuleId;
            }
        });
        return result;
    }

    public async ReleaseDataLabelingLock() {
        const amdId = this.audioMetadataId ?? this.lastLockAudioMetadataId;
        const licensedModuleId =
            this.selectedLicensedModuleId ?? this.lastLockLicensedModuleId;
        if (!amdId || !licensedModuleId) {
            runInAction(() => {
                this.hasDataLabelingLock = false;
            });
            return;
        }
        try {
            return await this.moduleService.ReleaseDataLabelingLock(
                amdId,
                licensedModuleId,
            );
        } finally {
            runInAction(() => {
                this.hasDataLabelingLock = false;
            });
        }
    }

    public async openModuleInLabelingDrawer(
        licensedModuleId: string,
    ): Promise<{ ok: boolean; lockError: boolean }> {
        runInAction(() => {
            this.selectedLicensedModuleId = licensedModuleId;
        });

        const acquiredLock = await this.TryGetDataLabelingLock();
        if (!acquiredLock) {
            return { ok: false, lockError: true };
        }

        await this.getConversationModuleInfo();
        runInAction(() => {
            this.selectedConversationModuleId = this.convoModuleInfo.find(
                (cmi) => cmi.licensedModuleId === licensedModuleId,
            )?.conversationModuleId;
        });

        await this.getLicensedModuleAndInitUiModel(licensedModuleId);
        return { ok: true, lockError: false };
    }

    // Walks the question tree (group questions nest their children under `questions`) looking for
    // any question configured for on-the-fly (Memory) autofill generation.
    private hasMemoryPersistenceQuestion(
        questions: Array<Question & { questions?: Question[] }> | undefined,
    ): boolean {
        return (questions ?? []).some(
            (question) =>
                question.automatedAnswerPersistenceMode ===
                    AnswerPersistenceMode.Memory ||
                this.hasMemoryPersistenceQuestion(question.questions),
        );
    }

    public async getLicensedModuleAndInitUiModel(id: string) {
        return this.setupAsyncTask(loadLicensedModule, async () => {
            const res = await this.moduleService.getLicensedModule(id);

            // Only generate autofill answers when the module actually has Memory-persistence
            // questions; otherwise the server does the same work just to return []. The persistence
            // mode is already on the questions we just fetched, so we can skip the round-trip.
            const generatedAnswers: Answer[] =
                this.hasMemoryPersistenceQuestion(res.questions)
                    ? await this.moduleService.generateAutofillAnswersForModule(
                          this.audioMetadataId!,
                          id,
                      )
                    : [];

            runInAction(() => {
                this.selectedLicensedModule = LicensedModule.fromJson(res);

                // Add Group Questions (nested questions) to the list of questions
                // in the LicensedModule
                this.selectedLicensedModule.questions = (
                    this.selectedLicensedModule.questions as Array<
                        Question & { questions?: Question[] }
                    >
                )
                    // Workaround for nested questions
                    // We have to place each grouped question into the list of questions
                    // because the backend does not return the grouped questions in the original list of questions
                    .flatMap((question) => {
                        if (
                            question.answerType.answerTypeName ===
                                AnswerTypeNames.QuestionGrouping &&
                            !!question.questions
                        ) {
                            const sortedGroupedQuestions =
                                question.questions.sort(
                                    (questionOne: any, questionTwo: any) =>
                                        questionOne.order - questionTwo.order,
                                );

                            sortedGroupedQuestions.forEach(
                                (groupedQuestion: Question) => {
                                    groupedQuestion.order = question.order;
                                },
                            );

                            return [question].concat(sortedGroupedQuestions);
                        }
                        return question;
                    });

                const existingAnswersForModule = this.convoModuleInfo.find(
                    (cmi) =>
                        cmi.conversationModuleId ===
                        this.selectedConversationModuleId,
                );

                const conversationModuleId =
                    existingAnswersForModule?.conversationModuleId ?? uuidv4();

                this.selectedConversationModuleId = conversationModuleId;

                const { answers, autofilledAnswerSnapshots } =
                    this.buildAnswersForSelectedModule(
                        existingAnswersForModule?.answers ?? [],
                        generatedAnswers,
                        this.selectedLicensedModule.questions,
                        conversationModuleId,
                    );

                this.answers = answers;
                this.autofilledAnswerSnapshots = autofilledAnswerSnapshots;

                this.uiModel = new ConvoModuleUIModel(
                    this.selectedLicensedModule.questions,
                    res,
                    [],
                    conversationModuleId,
                );
            });
            return res;
        });
    }

    public isQuestionAutofilled(questionId: string) {
        const snapshot = this.autofilledAnswerSnapshots.get(questionId);
        if (snapshot === undefined) {
            return false;
        }
        const currentAnswer = this.answers.find(
            (a) => a.questionId === questionId,
        );
        return this.getAnswerSignature(currentAnswer) === snapshot;
    }

    private getAnswerSignature(answer: Answer | undefined): string {
        if (!answer) {
            return "";
        }
        const tagIds = (answer.activeTags ?? [])
            .map((tag) => tag.id ?? "")
            .filter((id) => id.length > 0)
            .sort();
        const fillIn = answer.fillInAnswerValue ?? "";
        return `${tagIds.join(",")}|${fillIn}`;
    }

    private buildAnswersForSelectedModule(
        persistedAnswers: Answer[],
        generatedAnswers: Answer[],
        questions: Question[],
        conversationModuleId: string,
    ) {
        const formattedPersistedAnswers = persistedAnswers.map((answer) =>
            this.formatAnswerForSelectedModule(
                answer,
                questions,
                conversationModuleId,
            ),
        );

        const formattedGeneratedAnswers = generatedAnswers.map((answer) =>
            this.normalizeGeneratedAnswer(
                this.formatAnswerForSelectedModule(
                    answer,
                    questions,
                    conversationModuleId,
                ),
            ),
        );

        const answersByQuestionId = new Map<string, Answer>();

        formattedGeneratedAnswers.forEach((answer) => {
            answersByQuestionId.set(answer.questionId, answer);
        });

        formattedPersistedAnswers.forEach((answer) => {
            answersByQuestionId.set(answer.questionId, answer);
        });

        // Capture the autofill signature for every generated answer, regardless of whether
        // a persisted answer also exists for the question. The indicator hides when the user
        // edits away from the autofilled value, so we need the snapshot on completed modules too.
        const autofilledAnswerSnapshots = new Map<string, string>();
        formattedGeneratedAnswers.forEach((answer) => {
            autofilledAnswerSnapshots.set(
                answer.questionId,
                this.getAnswerSignature(answer),
            );
        });

        return {
            answers: [...answersByQuestionId.values()],
            autofilledAnswerSnapshots,
        };
    }

    private formatAnswerForSelectedModule(
        answer: Answer,
        questions: Question[],
        conversationModuleId: string,
    ) {
        const formattedAnswer = Answer.fromJson(answer);
        formattedAnswer.conversationModuleId = conversationModuleId;
        const currentQuestion = questions.find(
            (question) => question.id === formattedAnswer.questionId,
        );
        if (currentQuestion) {
            formattedAnswer.setQuestion(currentQuestion);
        }
        formattedAnswer.answerTags = formattedAnswer.answerTags.map((tag) => {
            tag.tag = currentQuestion?.tags.find(
                (questionTag) => questionTag.id === tag.tagId,
            );
            return tag;
        });

        return formattedAnswer;
    }

    private normalizeGeneratedAnswer(answer: Answer) {
        answer.id = undefined as unknown as string;
        answer.answerTags = answer.answerTags.map((tag) => {
            tag.id = undefined as unknown as string;
            tag.answerId = undefined;
            return tag;
        });

        return answer;
    }

    public async getConversationLicensedModules() {
        return this.setupAsyncTask(loadDataLabelModules, async () => {
            const res =
                await this.moduleService.getConversationLicensedModules();
            runInAction(() => {
                const canViewDataLabelingModules =
                    this.authStore.canUserView("Data Labeling");
                const canViewResearchModules =
                    this.authStore.canUserView("Research");
                const canViewSafetyEventModules = this.authStore.canUserView(
                    "Safety Event Review",
                );

                const dataLabelingModules = res.filter(
                    (module) =>
                        module.moduleCategory.name === "Data Labeling" &&
                        canViewDataLabelingModules,
                );
                const researchModules = res.filter(
                    (module) =>
                        module.moduleCategory.name === "Research" &&
                        canViewResearchModules,
                );
                const safetyEventModules = res.filter(
                    (module) =>
                        module.moduleCategory.name === "Safety Event Review" &&
                        canViewSafetyEventModules,
                );

                this.conversationLicensedModules = [
                    ...dataLabelingModules,
                    ...researchModules,
                ];
                this.conversationSafetyEventLicensedModules =
                    safetyEventModules;
            });
        });
    }

    public async createConversationModule() {
        return this.setupAsyncTask(createOrUpdateConvoModule, async () => {
            if (
                !this.selectedConversationModuleId ||
                !this.selectedLicensedModule?.id ||
                !this.audioMetadataId
            )
                return;
            try {
                await this.conversationService.createConversationModule({
                    conversationModule: {
                        id: this.selectedConversationModuleId,
                        licensedModuleId: this.selectedLicensedModule?.id,
                        audioMetadataId: this.audioMetadataId,
                    },
                    answers: this.answers,
                });
                this.messageStore.logMessage(
                    "Conversation labeled.",
                    "success",
                );
            } catch (error) {
                if (error instanceof ServiceError) {
                    this.messageStore.logError(
                        `Failed to label conversation: ${
                            error.errorMessage ?? error.message
                        }`,
                    );
                } else {
                    this.messageStore.logError("Failed to label conversation.");
                }
                console.error(JSON.stringify(error));
            }
        });
    }

    public async updateConversationModule() {
        return this.setupAsyncTask(createOrUpdateConvoModule, async () => {
            if (
                !this.selectedConversationModuleId ||
                !this.selectedLicensedModule?.id ||
                !this.audioMetadataId
            )
                return;

            try {
                await this.conversationService.updateConversationModule({
                    conversationModuleId: this.selectedConversationModuleId,
                    answers: this.answers,
                });
                this.messageStore.logMessage(
                    "Conversation labeled.",
                    "success",
                );
            } catch (error) {
                if (error instanceof ServiceError) {
                    this.messageStore.logError(
                        `Failed to label conversation: ${
                            error.errorMessage ?? error.message
                        }`,
                    );
                } else {
                    this.messageStore.logError("Failed to label conversation.");
                }
            }
        });
    }

    isMissingRequiredAnswer(renderedQuestions: Question[]): boolean {
        let isMissing = false;
        if (renderedQuestions.length) {
            const requiredQuestions = renderedQuestions.filter(
                (q) => q.required && q.isActive,
            );

            const requiredAnswers = this.answers.filter((a) =>
                requiredQuestions.map((q) => q.id).includes(a.questionId),
            );

            requiredQuestions.forEach((q) => {
                const foundAns = requiredAnswers.find(
                    (a) => a.questionId === q.id,
                );

                if (!foundAns) {
                    isMissing = true;
                    // In this else if block, we want to check if the answer is a fill in answer
                    // If it is, we don't want to accidentally trigger the next else if so
                    // we need to check if the fillInAnswerValue is empty separately
                } else if (q.answerType.isFillInAnswer) {
                    if (!foundAns.fillInAnswerValue) {
                        isMissing = true;
                    }
                    // The isAnswered check DOES NOT work for a fill in answer when initially loading
                    // because the question attribute on the answer is not always set
                } else if (!foundAns.isAnswered) {
                    isMissing = true;
                }
            });
        }

        return isMissing;
    }
}
